import type { MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { Nullable } from 'corgi-types';
import { noop } from 'lodash-es';
import type { ResponsiveValue, WidthProps } from 'styled-system';

import { EventBusEvent } from '../enums/EventBus';
import eventBus from '../event-bus/EventBus';
import type { Color } from '../theme/colors/types';

export interface DrawerEventProps extends WidthProps {
  open?: boolean;
  title?: string | React.ReactNode;
  content?: React.ReactNode;
  html?: Nullable<string>;
  p?: string;
  backgroundColor?: ResponsiveValue<Color>;
  onCloseComplete?: () => void;
  withCloseIcon?: boolean;
  toggleEvent?: EventBusEvent;
  withBackButton?: boolean;
  backButtonAriaLabel?: string;
  incrementZIndex?: boolean;
  transitionDisabled?: boolean;
  eventType?: 'toggle' | 'update';
  drawerKey?: string;
  withCloseDisabled?: boolean;
  withBodyFocus?: boolean;
}

type UseOpenDrawerEventData = Omit<DrawerEventProps, 'open' | 'onClose'> & {
  autoOpen?: boolean;
} & (
    | { shouldUpdateContent?: false | undefined }
    | { shouldUpdateContent: true; drawerKey: string }
  );

type EventOptions = {
  preventDefault?: boolean;
  stopPropagation?: boolean;
};

interface UseCloseDrawerOptions {
  toggleEvent?: EventBusEvent;
  callback?: () => void;
  isOpen?: boolean;
  withBackNavigation?: boolean;
}

const emitEvent = (eventData: DrawerEventProps) => {
  eventBus.emit<DrawerEventProps>(eventData.toggleEvent || EventBusEvent.ToggleDrawer, {
    open: true,
    eventType: 'toggle',
    ...eventData,
    ...(eventData.content && {
      content: eventData.content,
    }),
  });
};

export const useOpenDrawer = (
  eventData: UseOpenDrawerEventData,
  eventOptions: EventOptions = {}
) => {
  const openDrawer = useCallback(
    (event?: MouseEvent) => {
      const { preventDefault, stopPropagation } = eventOptions;
      if (preventDefault && event) {
        event.preventDefault();
      }
      if (stopPropagation && event) {
        event.stopPropagation();
      }

      emitEvent(eventData);
    },
    [eventData]
  );

  useEffect(() => {
    if (eventData.shouldUpdateContent) {
      emitEvent({
        ...eventData,
        eventType: 'update',
      });
    }
  }, [eventData.shouldUpdateContent, eventData.drawerKey, eventData.content]);

  useEffect(() => {
    if (eventData.autoOpen) {
      setTimeout(() => {
        openDrawer();
      }, 0);
    }
  }, []);

  return openDrawer;
};

export const useCloseDrawer = (options: UseCloseDrawerOptions = {}) => {
  const { toggleEvent = EventBusEvent.ToggleDrawer, callback = noop } = options;

  const closeDrawer = useCallback(() => {
    eventBus.emit(toggleEvent, { open: false });
    callback();
  }, [toggleEvent, callback]);

  return closeDrawer;
};

export const useGetOpenDrawer = () => {
  const [eventProps, setEventProps] = useState<UseOpenDrawerEventData>(null);
  const openDrawer = useOpenDrawer({
    ...eventProps,
    onCloseComplete: () => {
      setEventProps(null);
      eventProps?.onCloseComplete?.();
    },
  });

  useEffect(() => {
    if (eventProps) {
      openDrawer();
    }
  }, [eventProps]);

  return setEventProps;
};
